import React, { useMemo, useState } from 'react';
import { Modal } from '@heartinternet/hi-ui';
import { Text } from '@heartinternet/hi-ui';
import { Link } from '@heartinternet/hi-ui';
import { Button } from '@heartinternet/hi-ui';
import { CopyButton } from '@heartinternet/hi-ui';
import { Tooltip } from '@heartinternet/hi-ui';
import { Icon } from '@heartinternet/hi-ui';
import { Pagination } from '@heartinternet/hi-ui';
import type { ServiceDomain } from '../types';
import './DomainsModal.css';

interface DomainsModalProps {
  isOpen: boolean;
  onClose: () => void;
  domains: ServiceDomain[];
  primaryDomain: string;
  ipAddress: string | null;
  packageType: string;
  isWordpress: boolean;
}

const ITEMS_PER_PAGE = 8;

export const DomainsModal: React.FC<DomainsModalProps> = ({
  isOpen,
  onClose,
  domains,
  primaryDomain,
  ipAddress,
  packageType,
  isWordpress,
}) => {
  const [currentPage, setCurrentPage] = useState(1);

  const sortedDomains = useMemo(
    () => [...domains].sort((a, b) => a.domain_name.localeCompare(b.domain_name)),
    [domains]
  );

  const pageDomains = useMemo(
    () => sortedDomains.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE),
    [sortedDomains, currentPage]
  );

  const formatType = (type: string) => (type ? type.replace(/\./g, ' ') : '');

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="medium" title="">
      <div className="hi-ui-domains-modal">
        <div className="hi-ui-domains-modal__type-header">
          <Text variant="label">{isWordpress ? 'WP' : 'CP'}</Text>
          <div className="hi-ui-package-table__package-badge-border">
            <span className="hi-ui-package-table__package-badge">{formatType(packageType)}</span>
          </div>
        </div>

        <Text variant="body-large" bold inline>
          Domains assigned to this package
        </Text>

        <div>
          <div className="hi-ui-domains-modal__detail">
            <Text variant="body-small">Primary domain: </Text>
            <Text variant="body-small" bold>
              {primaryDomain}
            </Text>
          </div>

          {ipAddress && (
            <div className="hi-ui-domains-modal__detail">
              <Text variant="body-small">IP address: </Text>
              <Text variant="body-small" bold>
                {ipAddress}
              </Text>
              <CopyButton text={ipAddress} />
            </div>
          )}

          <Text variant="body-small">
            Manage DNS for your domains here. Changes may take up to 24 hours to propagate.
          </Text>
          <Text variant="body-small" inline>
            If a domain shows Unavailable, its DNS can&apos;t be edited here. You may need to update
            it to Heart Internet nameservers first.{' '}
            <Link
              href="https://heartinternet.uk/support/article/how-do-i-change-the-nameservers-for-my-domain.html"
              external
            >
              How to change your nameservers.
            </Link>
          </Text>
        </div>

        {domains.length > ITEMS_PER_PAGE && (
          <div className="hi-ui-domains-modal__list-header">
            <Pagination
              currentPage={currentPage}
              pageSize={ITEMS_PER_PAGE}
              totalItems={domains.length}
              onPageChange={setCurrentPage}
            />
          </div>
        )}

        <ul className="hi-ui-domains-modal__list">
          {pageDomains.map((domain) => (
            <li key={domain.id} className="hi-ui-domains-modal__list-item">
              <div className="hi-ui-domains-modal__domain-name">
                <Text variant="body" inline className="hi-ui-domains-modal__domain-text">
                  {domain.domain_name}
                </Text>
                {!domain.is_manageable && (
                  <Tooltip content="This domain is not registered with us.">
                    <Icon
                      name="question-red"
                      size={16}
                      className="hi-ui-domains-modal__info-icon"
                    />
                  </Tooltip>
                )}
              </div>
              {domain.is_manageable ? (
                <Button href={`/domains/${domain.id}/dns.cgi`} size="small">
                  Manage DNS
                </Button>
              ) : (
                <Button size="small" disabled>
                  Unavailable
                </Button>
              )}
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
};
